import * as THREE from 'three';

type Builder = () => THREE.Group;

function mat(color: string, rough = 0.8): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.05 });
}

function box(w: number, h: number, d: number, color: string): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function cyl(r: number, h: number, color: string, segs = 16): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, segs), mat(color));
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function sphere(r: number, color: string): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.SphereGeometry(r, 20, 16), mat(color));
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

const bed: Builder = () => {
  const g = new THREE.Group();
  const frame = box(1.4, 0.3, 2.2, '#b890ff');
  frame.position.y = 0.15;
  g.add(frame);
  const mattress = box(1.3, 0.2, 2.0, '#fff0fa');
  mattress.position.y = 0.4;
  g.add(mattress);
  const pillow = box(1.1, 0.12, 0.4, '#ffe0f0');
  pillow.position.set(0, 0.56, -0.7);
  g.add(pillow);
  const blanket = box(1.2, 0.08, 1.3, '#ff7aa8');
  blanket.position.set(0, 0.52, 0.25);
  g.add(blanket);
  const headboard = box(1.4, 0.9, 0.1, '#9970dd');
  headboard.position.set(0, 0.7, -1.1);
  g.add(headboard);
  return g;
};

const table: Builder = () => {
  const g = new THREE.Group();
  const top = cyl(0.7, 0.1, '#d8a87a', 24);
  top.position.y = 0.9;
  g.add(top);
  const leg = cyl(0.1, 0.9, '#b08060');
  leg.position.y = 0.45;
  g.add(leg);
  const base = cyl(0.4, 0.08, '#b08060');
  base.position.y = 0.04;
  g.add(base);
  return g;
};

const chair: Builder = () => {
  const g = new THREE.Group();
  const seat = box(0.5, 0.08, 0.5, '#d8a87a');
  seat.position.y = 0.5;
  g.add(seat);
  const back = box(0.5, 0.7, 0.08, '#b08060');
  back.position.set(0, 0.85, -0.2);
  g.add(back);
  for (const [x, z] of [[0.2, 0.2], [-0.2, 0.2], [0.2, -0.2], [-0.2, -0.2]]) {
    const leg = box(0.07, 0.5, 0.07, '#8a5e42');
    leg.position.set(x, 0.25, z);
    g.add(leg);
  }
  return g;
};

const rug: Builder = () => {
  const g = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.CircleGeometry(1.1, 32),
    new THREE.MeshStandardMaterial({ color: '#ffb8d1', roughness: 1 }),
  );
  base.rotation.x = -Math.PI / 2;
  base.position.y = 0.02;
  base.receiveShadow = true;
  g.add(base);
  const inner = new THREE.Mesh(
    new THREE.CircleGeometry(0.7, 32),
    new THREE.MeshStandardMaterial({ color: '#fff0fa', roughness: 1 }),
  );
  inner.rotation.x = -Math.PI / 2;
  inner.position.y = 0.03;
  g.add(inner);
  return g;
};

const lamp: Builder = () => {
  const g = new THREE.Group();
  const base = cyl(0.22, 0.08, '#9970dd');
  base.position.y = 0.04;
  g.add(base);
  const pole = cyl(0.04, 1.2, '#b8a8d8');
  pole.position.y = 0.64;
  g.add(pole);
  const shade = new THREE.Mesh(
    new THREE.ConeGeometry(0.35, 0.45, 20, 1, true),
    new THREE.MeshStandardMaterial({
      color: '#ffefa8',
      emissive: '#ffd98a',
      emissiveIntensity: 0.5,
      side: THREE.DoubleSide,
      roughness: 0.6,
    }),
  );
  shade.position.y = 1.3;
  g.add(shade);
  const light = new THREE.PointLight('#ffd98a', 0.8, 6, 2);
  light.position.y = 1.2;
  g.add(light);
  return g;
};

const bookshelf: Builder = () => {
  const g = new THREE.Group();
  const frame = box(1.2, 2.0, 0.4, '#8a5e42');
  frame.position.y = 1.0;
  g.add(frame);
  const colors = ['#ff7aa8', '#cfbfff', '#b8f2d8', '#ffd98a', '#8fd8ff'];
  for (let shelf = 0; shelf < 3; shelf++) {
    const y = 0.45 + shelf * 0.55;
    for (let i = 0; i < 6; i++) {
      const b = box(0.13, 0.35 + Math.random() * 0.1, 0.25, colors[(i + shelf) % colors.length]);
      b.position.set(-0.45 + i * 0.18, y, 0.08);
      g.add(b);
    }
  }
  return g;
};

const pottedPlant: Builder = () => {
  const g = new THREE.Group();
  const pot = cyl(0.3, 0.4, '#d87a5a', 20);
  pot.position.y = 0.2;
  g.add(pot);
  for (let i = 0; i < 5; i++) {
    const leaf = sphere(0.25 + Math.random() * 0.08, '#6ed49a');
    leaf.position.set(
      (Math.random() - 0.5) * 0.3,
      0.55 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.3,
    );
    leaf.scale.y = 1.4;
    g.add(leaf);
  }
  const flower = sphere(0.12, '#ff7aa8');
  flower.position.y = 0.95;
  g.add(flower);
  return g;
};

const painting: Builder = () => {
  const g = new THREE.Group();
  const frame = box(0.9, 0.7, 0.05, '#d6b168');
  frame.position.y = 0;
  g.add(frame);
  const canvas = box(0.8, 0.6, 0.08, '#ffe8f2');
  canvas.position.z = 0.01;
  g.add(canvas);
  const accent = sphere(0.15, '#ff7aa8');
  accent.position.set(-0.15, 0.05, 0.06);
  accent.scale.set(1, 1, 0.1);
  g.add(accent);
  const accent2 = sphere(0.1, '#cfbfff');
  accent2.position.set(0.15, -0.1, 0.06);
  accent2.scale.set(1, 1, 0.1);
  g.add(accent2);
  g.position.y = 1.5;
  return g;
};

const cushion: Builder = () => {
  const g = new THREE.Group();
  const body = box(0.7, 0.25, 0.7, '#cfbfff');
  body.position.y = 0.12;
  g.add(body);
  return g;
};

const mushroomStool: Builder = () => {
  const g = new THREE.Group();
  const stem = cyl(0.18, 0.5, '#fff0d7');
  stem.position.y = 0.25;
  g.add(stem);
  const cap = new THREE.Mesh(
    new THREE.SphereGeometry(0.42, 20, 16, 0, Math.PI * 2, 0, Math.PI / 2),
    mat('#ff7aa8', 0.7),
  );
  cap.position.y = 0.5;
  cap.castShadow = true;
  g.add(cap);
  for (let i = 0; i < 6; i++) {
    const dot = sphere(0.05, '#ffffff');
    const a = (i / 6) * Math.PI * 2;
    dot.position.set(Math.cos(a) * 0.25, 0.6 + Math.random() * 0.1, Math.sin(a) * 0.25);
    g.add(dot);
  }
  return g;
};

const crystalOrb: Builder = () => {
  const g = new THREE.Group();
  const base = cyl(0.2, 0.1, '#9970dd');
  base.position.y = 0.05;
  g.add(base);
  const orb = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 20, 16),
    new THREE.MeshStandardMaterial({
      color: '#cfbfff',
      emissive: '#a47bff',
      emissiveIntensity: 0.6,
      roughness: 0.15,
      metalness: 0.3,
    }),
  );
  orb.position.y = 0.28;
  orb.castShadow = true;
  g.add(orb);
  const glow = new THREE.PointLight('#a47bff', 0.5, 3, 2);
  glow.position.y = 0.28;
  g.add(glow);
  return g;
};

const fountain: Builder = () => {
  const g = new THREE.Group();
  const basin = cyl(0.9, 0.15, '#d8d4e8', 32);
  basin.position.y = 0.08;
  g.add(basin);
  const water = new THREE.Mesh(
    new THREE.CylinderGeometry(0.8, 0.8, 0.05, 32),
    new THREE.MeshStandardMaterial({ color: '#8fd8ff', roughness: 0.3, transparent: true, opacity: 0.8 }),
  );
  water.position.y = 0.17;
  g.add(water);
  const pillar = cyl(0.15, 0.5, '#d8d4e8');
  pillar.position.y = 0.45;
  g.add(pillar);
  const top = sphere(0.2, '#cfbfff');
  top.position.y = 0.8;
  g.add(top);
  return g;
};

export const FURNITURE_BUILDERS: Record<string, Builder> = {
  bed, table, chair, rug, lamp, bookshelf, pottedPlant, painting, cushion,
  mushroomStool, crystalOrb, fountain,
};
